/** SUNTRONIX ONE — Tenant Activation & Readiness (admin certification).
 *  Surfaces the Tenant Feature Registry, per-tenant Readiness (§85) and
 *  Pre-Demo Check (§86). Super-admin picks a tenant; a tenant admin sees own.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertTriangle, XCircle, ShieldCheck, ListChecks, Layers } from "lucide-react";

const ICON = {
  ACTIVE: <CheckCircle2 className="h-4 w-4 text-emerald-600" />,
  READY: <CheckCircle2 className="h-4 w-4 text-emerald-600" />,
  SETUP_REQUIRED: <AlertTriangle className="h-4 w-4 text-amber-500" />,
  WARNING: <AlertTriangle className="h-4 w-4 text-amber-500" />,
  TEMPORARILY_DISABLED: <AlertTriangle className="h-4 w-4 text-slate-400" />,
  UNAVAILABLE_FOR_ROLE: <XCircle className="h-4 w-4 text-slate-300" />,
  BLOCKER: <XCircle className="h-4 w-4 text-rose-600" />,
  ERROR: <XCircle className="h-4 w-4 text-rose-600" />,
};

export default function TenantReadiness() {
  const [tenant, setTenant] = useState("mc_one_demo");
  const [reg, setReg] = useState(null);
  const [ready, setReady] = useState(null);
  const [pre, setPre] = useState(null);

  const load = useCallback(() => {
    const t = tenant ? `?tenant=${encodeURIComponent(tenant)}` : "";
    api.get("/tenant/registry").then(({ data }) => setReg(data)).catch(() => {});
    api.get(`/tenant/readiness${t}`).then(({ data }) => setReady(data)).catch(() => {});
    api.get(`/tenant/pre-check${t}`).then(({ data }) => setPre(data)).catch(() => {});
  }, [tenant]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-6" data-testid="tenant-readiness-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-indigo-600 grid place-items-center text-white"><ShieldCheck className="h-5 w-5" /></div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Tenant Activation & Readiness</h1>
          <p className="text-[13px] text-slate-500">Feature registry, per-tenant readiness and pre-demo certification.</p>
        </div>
        <Input value={tenant} onChange={(e) => setTenant(e.target.value)} placeholder="master_company_id" className="w-56" data-testid="tenant-input" />
        <Button variant="outline" onClick={load} data-testid="tenant-load">Load</Button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-[13px] font-semibold text-slate-700 flex items-center gap-1.5"><ListChecks className="h-4 w-4" /> Tenant Readiness</h2>
            {ready && <span className={"text-[11px] font-bold px-2 py-0.5 rounded-full " + (ready.overall.includes("READY") ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-rose-50 text-rose-700 border border-rose-200")} data-testid="tenant-readiness-overall">{ready.overall}</span>}
          </div>
          <div className="rounded-xl border border-slate-200 bg-white divide-y" data-testid="tenant-readiness-list">
            {ready?.sections?.map((s) => (
              <div key={s.section} className="flex items-center gap-2 px-3 py-2">
                {ICON[s.status] || ICON.WARNING}
                <span className="text-[13px] font-medium text-slate-800 w-32">{s.section}</span>
                <span className="text-[11px] text-slate-500 flex-1">{s.detail}</span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-[13px] font-semibold text-slate-700 flex items-center gap-1.5"><ListChecks className="h-4 w-4" /> Pre-Demo Check</h2>
            {pre && <span className={"text-[11px] font-bold " + (pre.result === "READY" ? "text-emerald-600" : "text-rose-600")} data-testid="tenant-precheck-result">{pre.result}</span>}
          </div>
          <div className="rounded-xl border border-slate-200 bg-white divide-y max-h-[460px] overflow-y-auto" data-testid="tenant-precheck-list">
            {pre?.checks?.map((c) => (
              <div key={c.section} className="flex items-center gap-2 px-3 py-2">
                {ICON[c.status] || ICON.WARNING}
                <span className="text-[13px] text-slate-800 flex-1">{c.section}</span>
                {c.detail && <span className="text-[10px] text-slate-400">{c.detail}</span>}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-[13px] font-semibold text-slate-700 flex items-center gap-1.5"><Layers className="h-4 w-4" /> Tenant Feature Registry</h2>
          {reg && <span className="text-[11px] text-slate-500">{reg.active}/{reg.total} active for role {reg.role} · {reg.hidden_active?.length || 0} hidden</span>}
        </div>
        <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-[12.5px]" data-testid="registry-table">
            <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-500">
              {["Feature", "Status", "Roles", "Nav", "Dependency"].map((h) => <th key={h} className="px-3 py-2 font-semibold">{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {reg?.features?.map((f) => (
                <tr key={f.feature} className="hover:bg-slate-50" data-testid={`registry-${f.feature}`}>
                  <td className="px-3 py-2 text-slate-800">{f.label}</td>
                  <td className="px-3 py-2"><span className="inline-flex items-center gap-1">{ICON[f.status] || ICON.WARNING}<span className="text-[11px] text-slate-600">{f.status.replace(/_/g, " ")}</span></span></td>
                  <td className="px-3 py-2 text-[10px] text-slate-400">{(f.role_requirement || []).map((r) => r.replace("customer_", "")).join(", ")}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{f.nav_location || "—"}</td>
                  <td className="px-3 py-2 text-[10px] text-slate-400">{f.dependency || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
